import { useEffect, useState } from "react";
import { crmApi, type DuplicateMatch } from "../../lib/crmApi";
import { useLocale } from "../../shell/LocaleProvider";
import { DetailSection, FieldItem } from "./RecordWorkspace";

export type AuditStamps = {
  created_at?: string | null;
  created_by_email?: string | null;
  updated_at?: string | null;
  updated_by_email?: string | null;
};

/** Who wrote a record: the user's email, or a dash for rows that predate tracking. */
export function stampUser(email?: string | null): string {
  return email || "—";
}

/** Read-only created / modified block shown on company, contact, product and agreement screens. */
export function RecordAuditInfo({ record }: { record: AuditStamps }) {
  const { formatDateTime } = useLocale();
  return (
    <DetailSection title="Record information">
      <FieldItem label="Created by" value={stampUser(record.created_by_email)} />
      <FieldItem label="Created" value={formatDateTime(record.created_at)} />
      <FieldItem label="Modified by" value={stampUser(record.updated_by_email)} />
      <FieldItem label="Modified" value={formatDateTime(record.updated_at)} />
    </DetailSection>
  );
}

/** Warns (never blocks) when the person being typed already exists at the company. */
export function useContactDuplicates(
  orgId: string,
  query: { company_id: string; email: string; first_name: string; last_name: string; mobile?: string },
  excludeId?: string | null,
): DuplicateMatch | null {
  const [duplicates, setDuplicates] = useState<DuplicateMatch | null>(null);
  const { company_id, email, first_name, last_name, mobile = "" } = query;
  useEffect(() => {
    const hasKey = email.trim() || mobile.replace(/\D/g, "").length >= 7 || (first_name.trim() && last_name.trim());
    if (!company_id || !hasKey) {
      setDuplicates(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .findContactDuplicates(orgId, {
          company_id,
          email: email.trim() || undefined,
          first_name: first_name.trim() || undefined,
          last_name: last_name.trim() || undefined,
          mobile: mobile.trim() || undefined,
        })
        .then((result) =>
          setDuplicates({ ...result, matches: result.matches.filter((match) => match.id !== excludeId) }),
        )
        .catch(() => setDuplicates(null));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [orgId, company_id, email, first_name, last_name, mobile, excludeId]);
  return duplicates;
}

export function ContactDuplicateWarning({ duplicates }: { duplicates: DuplicateMatch | null }) {
  if (!duplicates || duplicates.matches.length === 0) return null;
  const names = duplicates.matches
    .map((match) => {
      const name = `${match.first_name ?? ""} ${match.last_name ?? ""}`.trim();
      const mail = match.work_email ?? match.email ?? match.personal_email;
      return mail ? `${name} (${mail})` : name;
    })
    .join(", ");
  return (
    <p className="crm-warning">
      This company already has a matching contact: {names}. You can still save to create another.
    </p>
  );
}
